import React, { useState } from 'react';
import { Persona } from '../../types/persona';
import { PersonaAvatar } from './PersonaAvatar';
import { PersonaCapabilityMeter } from './PersonaCapabilityMeter';
import { useTheme } from '../../contexts/ThemeContext';
import { getCapabilityColor } from '../../utils/colorUtils';
import { PersonaMarkdown } from './PersonaMarkdown';
import { FiUpload, FiDownload } from 'react-icons/fi';
import { useToast } from '../../contexts/ToastContext';
import * as personaService from '../../services/personaService';

interface PersonaDetailProps {
  persona: Persona;
  onClose: () => void;
  onTagClick?: (tag: string) => void;
  onUsePersona?: (personaId: string) => void;
}

export const PersonaDetail: React.FC<PersonaDetailProps> = ({ 
  persona, 
  onClose,
  onTagClick,
  onUsePersona
}) => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'capabilities' | 'patterns' | 'config' | 'prompt'>('overview');
  
  // Add this function to handle persona import
  const handleImportPersona = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        const results = await personaService.importPersonas(file);
        showToast(results.message, 'success');

        // Close the detail view
        onClose();
      } catch (error) {
        console.error('Import error:', error);
        showToast('There was an error importing the persona', 'error');
      }
    };
    fileInput.click();
  };
  
  if (!persona) return null;

  // Get primary specialization from tags if not explicitly set
  const primarySpecialization = persona.primarySpecialization || 
    (persona.tags && persona.tags.length > 0 ? persona.tags[0] : 'general');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200`}>
        <div className={`p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} relative overflow-hidden`}>
          {/* Decorative elements for razzle dazzle */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500 to-purple-600 rounded-full opacity-10 transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-green-500 to-cyan-600 rounded-full opacity-10 transform -translate-x-1/2 translate-y-1/2"></div>
          
          <div className="flex justify-between items-start relative z-10">
            <div className="flex items-center">
              <div className="relative">
                <PersonaAvatar 
                  name={persona.name} 
                  specialization={primarySpecialization}
                  avatarUrl={persona.avatarUrl}
                  size="large"
                />
                <div className="absolute -bottom-1 -right-1 bg-green-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-2 border-white dark:border-gray-900">
                  <span className="transform rotate-45">✓</span>
                </div>
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">{persona.name}</h2>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} flex items-center`}>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                  {primarySpecialization}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {persona.tags.slice(0, 5).map((tag, index) => (
                    <button
                      key={`tag-${index}`}
                      onClick={() => onTagClick && onTagClick(tag)}
                      className={`px-2 py-0.5 ${
                        darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } text-xs rounded-full transition-colors duration-200 hover:scale-105 transform`}
                    >
                      {tag}
                    </button>
                  ))}
                  {persona.tags.length > 5 && (
                    <span className={`px-2 py-0.5 ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    } text-xs rounded-full`}>
                      +{persona.tags.length - 5}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} hover:rotate-90 transform transition-transform duration-300`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex">
            <button
              className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                activeTab === 'overview'
                  ? darkMode 
                    ? 'border-b-2 border-green-500 text-green-400 bg-green-900 bg-opacity-20' 
                    : 'border-b-2 border-green-500 text-green-600 bg-green-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Overview
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                activeTab === 'capabilities'
                  ? darkMode 
                    ? 'border-b-2 border-purple-500 text-purple-400 bg-purple-900 bg-opacity-20' 
                    : 'border-b-2 border-purple-500 text-purple-600 bg-purple-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('capabilities')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Capabilities
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                activeTab === 'patterns'
                  ? darkMode 
                    ? 'border-b-2 border-amber-500 text-amber-400 bg-amber-900 bg-opacity-20' 
                    : 'border-b-2 border-amber-500 text-amber-600 bg-amber-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('patterns')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Patterns & Ethics
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                activeTab === 'prompt'
                  ? darkMode 
                    ? 'border-b-2 border-indigo-500 text-indigo-400 bg-indigo-900 bg-opacity-20' 
                    : 'border-b-2 border-indigo-500 text-indigo-600 bg-indigo-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('prompt')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                System Prompt
              </div>
            </button>
            
            <button
              className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                activeTab === 'config'
                  ? darkMode 
                    ? 'border-b-2 border-cyan-500 text-cyan-400 bg-cyan-900 bg-opacity-20' 
                    : 'border-b-2 border-cyan-500 text-cyan-600 bg-cyan-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('config')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Configuration
              </div>
            </button>
          </nav>
        </div>
        
        {/* Fixed height content area to prevent resizing */}
        <div className="p-6 overflow-y-auto" style={{ height: '400px' }}>
          {activeTab === 'overview' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Character Bible</h3>
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-4 rounded-lg border`}>
                  {/* Debug log to see persona structure */}
                  {console.log("PersonaDetail component data:", persona)}
                  <PersonaMarkdown 
                    content={
                      persona.fullDescription || 
                      persona.character_bible || 
                      (persona.data && typeof persona.data === 'object' ? persona.data.character_bible : null) ||
                      (persona.data && typeof persona.data === 'object' && persona.data.engineering_profile ? 
                        (persona.data.engineering_profile.current_state ? 
                          persona.data.engineering_profile.current_state.character_bible : null) : null) ||
                      persona.shortDescription || 
                      persona.prompt || 
                      "No character bible available."
                    }
                    size="medium"
                  />
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Tools</h3>
                <div className="flex flex-wrap gap-2">
                  {persona.allowedTools && persona.allowedTools.length > 0 ? (
                    persona.allowedTools.map((tool, index) => (
                      <span
                        key={`tool-${index}`}
                        className={`px-3 py-1 ${
                          darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                        } text-sm rounded-full`}
                      >
                        {tool}
                      </span>
                    ))
                  ) : (
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No specific tools assigned</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'capabilities' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Capabilities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {Object.entries(persona.capabilities).map(([key, value]) => (
                    <PersonaCapabilityMeter 
                      key={key}
                      label={key.charAt(0).toUpperCase() + key.slice(1)}
                      value={value}
                      max={10}
                    />
                  ))}
                </div>
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-4 rounded-lg border`}>
                  <h4 className="font-medium mb-2">Capability Descriptions</h4>
                  <dl className="space-y-3">
                    <div>
                      <dt className="font-medium text-gray-700">Creativity</dt>
                      <dd className="text-sm text-gray-600">Ability to generate novel ideas and solutions</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Technical</dt>
                      <dd className="text-sm text-gray-600">Knowledge of technical concepts and implementation details</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Analytical</dt>
                      <dd className="text-sm text-gray-600">Ability to analyze problems and evaluate solutions</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Communication</dt>
                      <dd className="text-sm text-gray-600">Clarity and effectiveness in expressing ideas</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'patterns' && (
            <div>
              {/* Three-column layout for patterns, antipatterns, and tech ethics */}
              <h3 className="text-lg font-semibold mb-4">Patterns, Antipatterns & Ethics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Patterns column */}
                <div>
                  <h4 className={`font-medium mb-2 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>Patterns</h4>
                  <div className={`${darkMode ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'} p-4 rounded-lg border h-full`}>
                    {persona.patterns && persona.patterns.length > 0 ? (
                      <ul className={`list-disc pl-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                        {persona.patterns.map((pattern, index) => (
                          <li key={index}>{pattern}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No patterns defined</p>
                    )}
                  </div>
                </div>
                
                {/* Antipatterns column */}
                <div>
                  <h4 className={`font-medium mb-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>Antipatterns</h4>
                  <div className={`${darkMode ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200'} p-4 rounded-lg border h-full`}>
                    {persona.antipatterns && persona.antipatterns.length > 0 ? (
                      <ul className={`list-disc pl-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                        {persona.antipatterns.map((antipattern, index) => (
                          <li key={index}>{antipattern}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No antipatterns defined</p>
                    )}
                  </div>
                </div>
                
                {/* Tech Ethics column */}
                <div>
                  <h4 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Technical Ethics</h4>
                  <div className={`${darkMode ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200'} p-4 rounded-lg border h-full`}>
                    {persona.tethics && persona.tethics.length > 0 ? (
                      <ul className={`list-disc pl-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                        {persona.tethics.map((tethic, index) => (
                          <li key={index}>{tethic}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No technical ethics defined</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'prompt' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">System Prompt</h3>
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-4 rounded-lg border relative`}>
                  <button 
                    className={`absolute top-2 right-2 p-1.5 rounded-md ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-700'}`}
                    onClick={() => {
                      navigator.clipboard.writeText(persona.prompt);
                    }}
                    title="Copy to clipboard"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-2M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <PersonaMarkdown 
                    content={persona.prompt}
                    size="medium"
                    maxHeight="300px"
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'config' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Configuration</h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>ID</p>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} font-mono text-sm`}>{persona.id}</p>
                </div>
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Primary Specialization</p>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{primarySpecialization}</p>
                </div>
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created</p>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{new Date(persona.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Last Updated</p>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{new Date(persona.updated_at).toLocaleString()}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">All Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {persona.tags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => onTagClick && onTagClick(tag)}
                      className={`${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                      } px-3 py-1 rounded-full text-sm`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 flex justify-end rounded-b-lg border-t`}>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className={`${
                darkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              } px-4 py-2 rounded transition-colors flex items-center`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
            
            <button
              onClick={() => window.open(`/personas/forge?personaId=${persona.id}`, '_blank')}
              className={`${
                darkMode 
                  ? 'bg-cyan-600 text-white hover:bg-cyan-700' 
                  : 'bg-cyan-500 text-white hover:bg-cyan-600'
              } px-4 py-2 rounded transition-colors flex items-center`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit in Forge
            </button>
            
            <button
              onClick={() => onUsePersona && onUsePersona(persona.id)}
              className={`${
                darkMode 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-green-500 text-white hover:bg-green-600'
              } px-4 py-2 rounded transition-colors flex items-center`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Use This Persona
            </button>
            
            {/* Import button */}
            <button
              onClick={handleImportPersona}
              className={`${
                darkMode 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-purple-500 text-white hover:bg-purple-600'
              } px-4 py-2 rounded transition-colors flex items-center`}
            >
              <FiUpload className="mr-1.5" />
              Import Persona
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
